import React from "react"
import { useTranslation } from "react-i18next"
import {
  MessageSquare,
  Share2,
  Paperclip,
  Bell,
  Link as LinkIcon,
  FileCheck,
  Lock,
  ShieldCheck,
  BarChart3,
  Stethoscope,
  GraduationCap,
  Landmark,
  Globe2,
  Brain,
  FileText,
  ArrowRightCircle,
  Users2,
} from "lucide-react"
import { useNavigationStore } from "@/stores/navigation"
import CustomerLogos from "@/components/common/CustomerLogos"

const EConsultCX: React.FC = () => {
  const { t } = useTranslation()
  const navigationStore = useNavigationStore()

  const features = [
    {
      title: t("econsultcx.features.items.asynchronousCommunication.title"),
      body: t("econsultcx.features.items.asynchronousCommunication.body"),
      Icon: MessageSquare,
    },
    {
      title: t("econsultcx.features.items.smartRouting.title"),
      body: t("econsultcx.features.items.smartRouting.body"),
      Icon: Share2,
    },
    {
      title: t("econsultcx.features.items.flexibleAttachments.title"),
      body: t("econsultcx.features.items.flexibleAttachments.body"),
      Icon: Paperclip,
    },
    {
      title: t("econsultcx.features.items.statusNotifications.title"),
      body: t("econsultcx.features.items.statusNotifications.body"),
      Icon: Bell,
    },
    {
      title: t("econsultcx.features.items.convertToReferral.title"),
      body: t("econsultcx.features.items.convertToReferral.body"),
      Icon: ArrowRightCircle,
    },
    {
      title: t("econsultcx.features.items.worksStandaloneOrWithEhr.title"),
      body: t("econsultcx.features.items.worksStandaloneOrWithEhr.body"),
      Icon: LinkIcon,
    },
  ]

  const useCases = [
    {
      title: t("econsultcx.useCases.items.pcpToSpecialistTriage.title"),
      body: t("econsultcx.useCases.items.pcpToSpecialistTriage.body"),
      Icon: Stethoscope,
    },
    {
      title: t("econsultcx.useCases.items.schoolCorrectionalHealth.title"),
      body: t("econsultcx.useCases.items.schoolCorrectionalHealth.body"),
      Icon: GraduationCap,
    },
    {
      title: t("econsultcx.useCases.items.publicHealthGuidance.title"),
      body: t("econsultcx.useCases.items.publicHealthGuidance.body"),
      Icon: Landmark,
    },
    {
      title: t("econsultcx.useCases.items.secondOpinions.title"),
      body: t("econsultcx.useCases.items.secondOpinions.body"),
      Icon: Brain,
    },
    {
      title: t("econsultcx.useCases.items.internationalConsults.title"),
      body: t("econsultcx.useCases.items.internationalConsults.body"),
      Icon: Globe2,
    },
    {
      title: t("econsultcx.useCases.items.patientParentToProvider.title"),
      body: t("econsultcx.useCases.items.patientParentToProvider.body"),
      Icon: Users2,
    },
  ]

  const whyBullets = [
    {
      title: t("econsultcx.whyEconsultcx.items.improveAccessSpeed.title"),
      details: t("econsultcx.whyEconsultcx.items.improveAccessSpeed.details", {
        returnObjects: true,
      }),
      Icon: Share2,
    },
    {
      title: t("econsultcx.whyEconsultcx.items.maximizeKnowledgeBase.title"),
      details: t(
        "econsultcx.whyEconsultcx.items.maximizeKnowledgeBase.details",
        { returnObjects: true }
      ),
      Icon: FileText,
    },
    {
      title: t("econsultcx.whyEconsultcx.items.simpleGuidedWorkflows.title"),
      details: t(
        "econsultcx.whyEconsultcx.items.simpleGuidedWorkflows.details",
        { returnObjects: true }
      ),
      Icon: MessageSquare,
    },
    {
      title: t(
        "econsultcx.whyEconsultcx.items.operateStandaloneOrExtension.title"
      ),
      details: t(
        "econsultcx.whyEconsultcx.items.operateStandaloneOrExtension.details",
        { returnObjects: true }
      ),
      Icon: LinkIcon,
    },
  ]

  const faqs = [
    {
      q: t("econsultcx.faq.items.integration.question"),
      a: t("econsultcx.faq.items.integration.answer"),
      Icon: LinkIcon,
    },
    {
      q: t("econsultcx.faq.items.security.question"),
      a: t("econsultcx.faq.items.security.answer"),
      Icon: ShieldCheck,
    },
    {
      q: t("econsultcx.faq.items.implementation.question"),
      a: t("econsultcx.faq.items.implementation.answer"),
      Icon: FileCheck,
    },
    {
      q: t("econsultcx.faq.items.consultTypes.question"),
      a: t("econsultcx.faq.items.consultTypes.answer"),
      Icon: MessageSquare,
    },
    {
      q: t("econsultcx.faq.items.convertToReferral.question"),
      a: t("econsultcx.faq.items.convertToReferral.answer"),
      Icon: ArrowRightCircle,
    },
    {
      q: t("econsultcx.faq.items.pricing.question"),
      a: t("econsultcx.faq.items.pricing.answer"),
      Icon: BarChart3,
    },
  ]

  const handleBookDemo = () => {
    navigationStore.openDemoModal()
  }

  return (
    <div className="min-h-screen w-full text-white">
      {/* STICKY MOBILE CTA */}
      <div className="fixed bottom-0 inset-x-0 z-50 bg-white/90 backdrop-blur border-t border-slate-200 p-3 sm:hidden">
        <div className="flex gap-3">
          <button
            onClick={handleBookDemo}
            className="flex-1 text-center bg-[#395091] text-white font-semibold px-4 py-3 rounded-lg shadow-md"
          >
            {t("common.bookDemo")}
          </button>
          <a
            href="#case-study"
            className="w-full sm:w-auto text-center bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/20 transition"
          >
            {t("common.downloadCaseStudy")}
          </a>
        </div>
      </div>

      {/* HERO + BODY COMBINED */}
      <main className="bg-white text-slate-800">
        <header className="w-full bg-gradient-to-r from-[#395091] to-[#559EBA]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-white">
            {/* Product Wordmark */}
            <div className="mb-8 sm:mb-10">
              <h2 className="text-6xl sm:text-7xl md:text-8xl leading-none tracking-tight">
                <span >eConsult</span>
                <span className="font-bold text-[#7AC943]">CX</span>
                <sup className="align-super text-2xl sm:text-3xl md:text-4xl ml-1">
                  TM
                </sup>
              </h2>
            </div>

            {/* Headline & Subcopy */}
            <div className="max-w-3xl">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
                {t("econsultcx.hero.title")}
              </h1>
              <p className="text-base sm:text-lg md:text-xl/8 opacity-95">
                {t("econsultcx.hero.subtitle")}
              </p>
              <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <button
                  onClick={handleBookDemo}
                  className="w-full sm:w-auto text-center bg-white text-[#395091] font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-gray-100 transition"
                >
                  {t("common.bookDemo")}
                </button>
                <a
                  href="#case-study"
                  className="w-full sm:w-auto text-center bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/20 transition"
                >
                  {t("common.downloadCaseStudy")}
                </a>
              </div>
            </div>
          </div>
        </header>

        {/* Features Section with Value-Driven Heading */}
        <section
          id="features"
          className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 bg-[#F3F6FB]"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
            {t("econsultcx.features.title")}
          </h3>
          <p className="text-slate-600 mb-6">
            {t("econsultcx.features.subtitle")}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {features.map(({ title, body, Icon }, i) => (
              <div
                key={i}
                className="rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm hover:shadow-md transition bg-white"
              >
                <div className="flex items-start gap-3 mb-2">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#E8F0FF]">
                    <Icon
                      className="w-5 h-5 text-[#395091]"
                      aria-hidden="true"
                    />
                  </span>
                  <h4 className="font-semibold text-slate-900 text-lg leading-snug">
                    {title}
                  </h4>
                </div>
                <p className="text-slate-600 text-base leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Use Cases */}
        <section
          id="use-cases"
          className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 bg-gradient-to-br from-[#E6F2FF] to-[#F0F5FF] rounded-3xl"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 sm:mb-8">
            {t("econsultcx.useCases.title")}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {useCases.map(({ title, body, Icon }, i) => (
              <div
                key={i}
                className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6 shadow-sm"
              >
                <div className="flex items-start gap-3 mb-2">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#E3ECFF]">
                    <Icon
                      className="w-5 h-5 text-[#395091]"
                      aria-hidden="true"
                    />
                  </span>
                  <h4 className="font-semibold text-slate-900 text-lg leading-snug">
                    {title}
                  </h4>
                </div>
                <p className="text-slate-600 text-base leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Case Study + Why */}
        <section
          id="case-study"
          className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 scroll-mt-24 md:scroll-mt-28"
        >
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-start">
            {/* Case Study */}
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3 sm:mb-4">
                {t("econsultcx.caseStudy.title")}
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                {t("econsultcx.caseStudy.description")}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <a
                  href="/use-case-finder?Org=STLUKE"
                  className="inline-block rounded-lg bg-[#395091] text-white px-5 py-3 font-semibold hover:opacity-90 text-center"
                >
                  {t("common.viewCaseStudy")}
                </a>
                <a
                  href="/use-case-finder"
                  className="inline-block text-[#395091] font-semibold hover:underline text-center"
                >
                  {t("econsultcx.caseStudy.seeMoreStories")}
                </a>
              </div>
              {/* Client logos row */}
              <CustomerLogos logoUids={["rady", "dfci", "nhs"]} />
            </div>

            {/* Why eConsultCX */}
            <div className="rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm bg-gradient-to-br from-[#EAF0FF] via-white to-[#F9FAFB]">
              <h4 className="font-bold text-slate-900 mb-3 text-3xl md:text-4xl">
                {t("econsultcx.whyEconsultcx.title")}
              </h4>
              <p className="text-slate-700 text-base leading-relaxed mb-4">
                {t("econsultcx.whyEconsultcx.description")}
              </p>
              <ul className="space-y-3 text-base">
                {whyBullets.map(({ title, details, Icon }, idx) => (
                  <li key={idx}>
                    <details className="group">
                      <summary className="cursor-pointer font-medium text-slate-900 flex items-center gap-2">
                        <Icon className="w-5 h-5 text-[#395091]" /> {title}
                      </summary>
                      <div className="mt-2 text-slate-600">
                        <ul className="list-disc ml-5 space-y-1">
                          {(details as string[]).map((d: string, i: number) => (
                            <li key={i}>{d}</li>
                          ))}
                        </ul>
                      </div>
                    </details>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Trust & Compliance */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 border-t border-slate-200 bg-white">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            {t("econsultcx.trust.title")}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center gap-2">
              <ShieldCheck className="w-8 h-8 text-[#395091]" />
              <span className="text-slate-700 font-medium">
                {t("econsultcx.trust.hipaa")}
              </span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <FileCheck className="w-8 h-8 text-[#395091]" />
              <span className="text-slate-700 font-medium">
                {t("econsultcx.trust.soc2")}
              </span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Lock className="w-8 h-8 text-[#395091]" />
              <span className="text-slate-700 font-medium">
                {t("econsultcx.trust.sso")}
              </span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <ShieldCheck className="w-8 h-8 text-[#395091]" />
              <span className="text-slate-700 font-medium">
                {t("econsultcx.trust.gdpr")}
              </span>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 border-t border-slate-200 bg-[#F3F6FB]">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            {t("econsultcx.faq.title")}
          </h3>
          <ul className="space-y-4">
            {faqs.map(({ q, a, Icon }, i) => (
              <li
                key={i}
                className="border border-slate-200 rounded-lg p-4 bg-white"
              >
                <details className="group">
                  <summary className="flex items-center gap-2 font-medium cursor-pointer text-slate-900">
                    <Icon className="w-5 h-5 text-[#395091]" /> {q}
                  </summary>
                  <p className="mt-2 text-slate-600 leading-relaxed">{a}</p>
                </details>
              </li>
            ))}
          </ul>
        </section>

        {/* Final CTA */}
        <section id="contact" className="relative mt-6 sm:mt-8">
          <div className="absolute inset-0 bg-gradient-to-r from-[#395091] to-[#559EBA]" />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 text-white">
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-center">
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">
                  {t("econsultcx.cta.title")}
                </h3>
                <p className="opacity-90 text-base sm:text-lg">
                  {t("econsultcx.cta.subtitle")}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 lg:justify-end w-full sm:w-auto">
                <button
                  onClick={handleBookDemo}
                  className="w-full sm:w-auto text-center bg-white text-[#395091] font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-gray-100 transition"
                >
                  {t("common.bookDemo")}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer note */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10 text-slate-500 text-sm">
          {t("econsultcx.footer")}
        </section>
      </main>
    </div>
  )
}

export default EConsultCX
